import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, ShieldAlert, Copy, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * PersistentMfaSetup — non-dismissible modal that fires whenever the
 * logged-in user's `user.mfa_setup_required` is set (e.g. by the admin
 * Reset MFA button in /admin/users → Security tab).
 *
 * Spec (admin-stated):
 *   • Must persist across tab close, page reload, and even logout.
 *     Closing the browser doesn't satisfy the requirement.
 *   • No X / cancel / dismiss controls. The ONLY way out is to finish
 *     enrolment for every factor listed in `mfa_setup_required.required`.
 *   • Forces TOTP first if listed, then Recovery Key (recovery key has
 *     to come AFTER TOTP so the user has something to authenticate
 *     against if they ever lose the recovery key).
 *
 * Persistence strategy:
 *   The flag lives on the server (`db.users[user_id].mfa_setup_required`).
 *   Every fresh auth/me response re-emits it, so logout-and-login
 *   doesn't help the user escape. The modal mounts in `Layout`
 *   regardless of route, so even direct-URL navigation can't bypass.
 */
export default function PersistentMfaSetup() {
  const { user, refreshUser, api } = useAuth();
  // Required factors come from either:
  //   • `user.mfa_setup_required` — set by admin "Reset MFA"
  //   • `user.pending_mfa_reset_after_bypass` — set after the user
  //     just logged in via an admin bypass; ONLY this flag carries
  //     the 2-minute setup deadline, so we surface a small countdown
  //     when it's the active trigger.
  let required = (user?.mfa_setup_required?.required) || [];
  const bypassPending = !!user?.pending_mfa_reset_after_bypass;
  if (bypassPending && !required.includes('totp')) required = ['totp', ...required];
  // For the bypass path the old secret is wiped server-side on
  // consume, so `!totp_enabled` is already true. We keep the check
  // explicit in case the flag is set without consume (defensive).
  const totpRequired = required.includes('totp') && (bypassPending || !user?.totp_enabled);
  const recoveryRequired = required.includes('recovery_key') && !user?.recovery_key_enabled;
  const needsAny = totpRequired || recoveryRequired;

  // TOTP setup state
  const [qrCode, setQrCode] = useState(null);
  const [totpSecret, setTotpSecret] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [loadingQr, setLoadingQr] = useState(false);
  const [verifying, setVerifying] = useState(false);

  // Recovery key state
  const [revealedRecoveryKey, setRevealedRecoveryKey] = useState(null);
  const [recoveryCopied, setRecoveryCopied] = useState(false);
  const [recoveryConfirmed, setRecoveryConfirmed] = useState(false);
  const [generatingRecovery, setGeneratingRecovery] = useState(false);

  // Decide which factor to render right now. TOTP-first ordering.
  const currentFactor = totpRequired ? 'totp' : (recoveryRequired ? 'recovery_key' : null);

  // Auto-fetch the QR when the TOTP step becomes active.
  const setupTotp = useCallback(async () => {
    if (qrCode) return;
    setLoadingQr(true);
    try {
      const res = await api.post('/mfa/setup-totp');
      setQrCode(res.data?.qr_code);
      setTotpSecret(res.data?.totp_secret);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start authenticator setup');
    } finally {
      setLoadingQr(false);
    }
  }, [api, qrCode]);

  useEffect(() => {
    if (currentFactor === 'totp') setupTotp();
  }, [currentFactor, setupTotp]);

  const verifyTotp = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Enter the 6-digit code');
      return;
    }
    setVerifying(true);
    try {
      if (bypassPending) {
        // Post-bypass path uses a dedicated endpoint that enforces
        // the 2-minute setup window server-side and atomically
        // promotes the pending secret to active.
        await api.post('/me/security/finalize-mfa-after-bypass', {
          totp_secret_pending: totpSecret,
          code: verificationCode,
        });
      } else {
        await api.post('/mfa/verify-totp', { code: verificationCode });
      }
      setVerificationCode('');
      setQrCode(null);
      setTotpSecret(null);
      await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Invalid code');
    } finally {
      setVerifying(false);
    }
  };

  const generateRecoveryKey = async () => {
    setGeneratingRecovery(true);
    try {
      const res = await api.post('/mfa/generate-recovery-key');
      setRevealedRecoveryKey(res.data?.recovery_key);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate recovery key');
    } finally {
      setGeneratingRecovery(false);
    }
  };

  const completeRecovery = async () => {
    // The user has saved the key; refresh the auth context so the
    // flag clears and the modal unmounts.
    await refreshUser();
  };

  // Block keyboard escape / browser back. The OS-level shortcut for
  // close/refresh can't be intercepted, but the flag re-applies on the
  // very next /auth/me call so the modal returns instantly.
  useEffect(() => {
    if (!needsAny) return;
    const blockKeys = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', blockKeys, true);
    return () => window.removeEventListener('keydown', blockKeys, true);
  }, [needsAny]);

  if (!user || !needsAny) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      data-testid="persistent-mfa-setup-overlay"
      // No `onClick` close — overlay click does nothing.
    >
      <div className="w-full max-w-md rounded-2xl bg-background border border-red-500/40 shadow-2xl p-6 space-y-4">
        {bypassPending && <BypassMfaTimer userTriggeredAt={user?.pending_mfa_reset_after_bypass?.triggered_at} />}
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-red-100 dark:bg-red-900/40 p-2">
            <ShieldAlert className="w-5 h-5 text-red-600 dark:text-red-300" />
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-base">Security Setup Required</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              An administrator reset your multi-factor authentication. You must complete the setup below before continuing.
            </p>
            <p className="text-[10px] text-red-700 dark:text-red-300 mt-2 font-semibold uppercase tracking-wider">
              This cannot be dismissed.
            </p>
          </div>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-1 text-[11px]">
          <span className={`px-2 py-1 rounded ${currentFactor === 'totp' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`} data-testid="mfa-step-totp">
            1. Authenticator
          </span>
          <span className="text-muted-foreground">→</span>
          <span className={`px-2 py-1 rounded ${currentFactor === 'recovery_key' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`} data-testid="mfa-step-recovery">
            2. Recovery Key
          </span>
        </div>

        {currentFactor === 'totp' && (
          <div className="space-y-3" data-testid="persistent-mfa-totp">
            <div className="rounded-lg bg-muted/40 p-3 text-xs space-y-2">
              <p className="font-semibold">Set up your Authenticator app</p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li>Open Google Authenticator / Authy / 1Password</li>
                <li>Scan this QR code</li>
                <li>Enter the 6-digit code below</li>
              </ol>
            </div>
            {loadingQr ? (
              <div className="py-6 text-center text-xs text-muted-foreground">Generating QR…</div>
            ) : qrCode ? (
              <div className="flex justify-center bg-white p-3 rounded-lg">
                <img src={qrCode} alt="QR Code" className="w-48 h-48" data-testid="persistent-mfa-qr" />
              </div>
            ) : null}
            {totpSecret && (
              <p className="text-[10px] text-center text-muted-foreground">
                Can't scan? Enter manually: <code className="bg-muted px-1 py-0.5 rounded">{totpSecret}</code>
              </p>
            )}
            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-1"><Lock className="w-3 h-3" /> 6-digit code</Label>
              <Input
                inputMode="numeric"
                pattern="\d*"
                maxLength={6}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                className="font-mono text-center tracking-widest"
                data-testid="persistent-mfa-code"
                onKeyDown={(e) => { if (e.key === 'Enter') verifyTotp(); }}
              />
            </div>
            <Button
              className="w-full bg-[#586D52] hover:bg-[#475a43]"
              onClick={verifyTotp}
              disabled={verifying || verificationCode.length < 6}
              data-testid="persistent-mfa-verify"
            >
              {verifying ? 'Verifying…' : 'Verify & Continue'}
            </Button>
          </div>
        )}

        {currentFactor === 'recovery_key' && (
          <div className="space-y-3" data-testid="persistent-mfa-recovery">
            <div className="rounded-lg bg-muted/40 p-3 text-xs space-y-2">
              <p className="font-semibold">Generate your Recovery Key</p>
              <p className="text-muted-foreground">
                Your recovery key lets you sign in if you lose access to your authenticator. Write it down somewhere safe — we can't show it to you again.
              </p>
            </div>
            {!revealedRecoveryKey ? (
              <Button
                className="w-full bg-[#586D52] hover:bg-[#475a43]"
                onClick={generateRecoveryKey}
                disabled={generatingRecovery}
                data-testid="persistent-mfa-generate-recovery"
              >
                {generatingRecovery ? 'Generating…' : 'Generate Recovery Key'}
              </Button>
            ) : (
              <>
                <div className="rounded-lg border-2 border-dashed border-amber-400 bg-amber-50 dark:bg-amber-900/20 p-4 text-center space-y-2">
                  <p className="font-mono font-bold text-xl tracking-widest break-all" data-testid="persistent-mfa-recovery-value">
                    {revealedRecoveryKey}
                  </p>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(revealedRecoveryKey);
                      setRecoveryCopied(true);
                      setTimeout(() => setRecoveryCopied(false), 2000);
                    }}
                    className="text-[11px] text-amber-700 dark:text-amber-300 inline-flex items-center gap-1 underline"
                  >
                    {recoveryCopied ? <><CheckCircle2 className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy to clipboard</>}
                  </button>
                </div>
                <label className="flex items-start gap-2 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={recoveryConfirmed}
                    onChange={(e) => setRecoveryConfirmed(e.target.checked)}
                    className="mt-0.5"
                    data-testid="persistent-mfa-recovery-confirm"
                  />
                  <span>I've saved this recovery key somewhere safe. I understand it will not be shown again.</span>
                </label>
                <Button
                  className="w-full bg-[#586D52] hover:bg-[#475a43]"
                  onClick={completeRecovery}
                  disabled={!recoveryConfirmed}
                  data-testid="persistent-mfa-recovery-done"
                >
                  Continue
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function BypassMfaTimer({ userTriggeredAt }) {
  // 2-minute countdown for post-bypass MFA setup. If user doesn't
  // finalize before it hits 0, the server-side window check rejects
  // any verify call, but we also want the UI to nudge them — and
  // ultimately force-logout if they don't act. Re-uses the
  // sessionStorage refresh-detection trick.
  const { logout } = useAuth();
  const [now, setNow] = useState(Date.now());
  const [startedAt] = useState(() => {
    try { return userTriggeredAt ? new Date(userTriggeredAt).getTime() : Date.now(); }
    catch (_) { return Date.now(); }
  });
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (now - startedAt > 300 * 1000) {
      toast.error('Setup window expired — admin must re-issue bypass');
      logout();
    }
  }, [now, startedAt, logout]);
  const remaining = Math.max(0, Math.ceil((300 * 1000 - (now - startedAt)) / 1000));
  const mm = String(Math.floor(remaining / 60)).padStart(1, '0');
  const ss = String(remaining % 60).padStart(2, '0');
  return (
    <div className="flex items-center justify-between rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-2.5 py-1.5" data-testid="bypass-mfa-timer">
      <p className="text-[11px] text-red-700 dark:text-red-300">Configure MFA within window. Override is single-use.</p>
      <span className="font-mono text-xs font-bold text-red-700 dark:text-red-300">{mm}:{ss}</span>
    </div>
  );
}

